import type { SupabaseClient } from '@supabase/supabase-js';

export interface JobProgress {
  processed?: number;
  total?: number;
  created?: number;
  matched?: number;
  unmatched?: number;
  failed?: number;
  listingsCreated?: number;
  listingFailures?: number;
  skipped?: number;
  alreadyMapped?: number;
  updated_at?: string;
}

export interface JobRow {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  attempts: number;
  result: unknown;
  error: string | null;
  progress: JobProgress | null;
}

export interface AwaitJobOptions {
  /** Default 30000 ms. Hartes Timeout: nach Ablauf wirft `awaitJob`. */
  timeoutMs?: number;
  /**
   * Callback wird bei jedem Poll-Tick aufgerufen, solange der Job noch nicht im Terminal-Status ist.
   * Erhält den frischen Job-Row inklusive `progress`-Spalte (gefüllt vom Worker).
   *
   * Wird auch genau einmal beim Übergang in den Terminal-Status aufgerufen — Caller kann darauf
   * verzichten, weil das Promise dann gleich resolved.
   */
  onProgress?: (job: JobRow) => void;
  /** Poll-Intervall in ms (Default 500). Bei Live-Progress-UI gerne hochsetzen (1000-2000). */
  pollIntervalMs?: number;
}

const DEFAULT_POLL_INTERVAL_MS = 500;

/**
 * Polls `account.jobs` für eine Job-ID, bis sie einen Terminal-Status erreicht
 * (completed/failed/cancelled) oder das Timeout fällt. Liest seit 0089 zusätzlich die
 * `progress`-Spalte — bei langlaufenden Bulk-Jobs ruft der Worker dort alle 3 Items einen
 * Zwischenstand rein, den der Caller per `onProgress`-Callback live anzeigen kann.
 *
 * Backward-compatible: 3. Argument darf weiterhin eine reine Number (timeoutMs) sein.
 */
export async function awaitJob(
  supabase: SupabaseClient,
  jobId: string,
  timeoutMsOrOpts?: number | AwaitJobOptions,
): Promise<JobRow> {
  const opts: AwaitJobOptions =
    typeof timeoutMsOrOpts === 'number'
      ? { timeoutMs: timeoutMsOrOpts }
      : (timeoutMsOrOpts ?? {});
  const timeoutMs = opts.timeoutMs ?? 30_000;
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .schema('account')
      .from('jobs')
      .select('id,status,attempts,result,error,progress')
      .eq('id', jobId)
      .maybeSingle();
    if (error) throw new Error(`awaitJob query failed: ${error.message}`);
    if (data) {
      const row = data as JobRow;
      opts.onProgress?.(row);
      if (row.status === 'completed' || row.status === 'failed' || row.status === 'cancelled') {
        return row;
      }
    }
    if (Date.now() > deadline) {
      throw new Error(`Job ${jobId} did not finish within ${timeoutMs} ms`);
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
}
